/**
 * useRole — read/write the user's intent (buying or owner) with cookie
 * persistence. Defaults to 'buyer' when nothing is stored, matching the
 * dominant traffic shape (most arrivals are pre-offer buyers).
 *
 * Per the UX research (memory `project-buyer-report-v2-design`, side note
 * 2026-05-29): we use ownership language, not transaction-stage language.
 * "I own this property" is honest for sellers who haven't decided to list
 * yet. We persist the role in a cookie (not the URL) so /property/:id
 * stays canonical for SEO; the URL only carries `?intent=sell` when we
 * navigate so PropertyFile can pick it up server-side-friendly.
 */

import { useCallback, useEffect, useState } from 'react';

export type Role = 'buyer' | 'owner';

const COOKIE_NAME = 'ukpc_role';
const COOKIE_MAX_AGE_DAYS = 90;

function readCookie(): Role | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.slice(COOKIE_NAME.length + 1);
  return value === 'owner' ? 'owner' : value === 'buyer' ? 'buyer' : null;
}

function writeCookie(role: Role): void {
  if (typeof document === 'undefined') return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  // SameSite=Lax matches our session-cookie policy. Path=/ so every page reads it.
  document.cookie = `${COOKIE_NAME}=${role}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function useRole(): {
  role: Role;
  setRole: (next: Role) => void;
} {
  const [role, setRoleState] = useState<Role>(() => readCookie() ?? 'buyer');

  // If another tab updated the cookie while this one was open, pick it up
  // on focus. Cheap; fires no requests.
  useEffect(() => {
    const onFocus = () => {
      const fromCookie = readCookie();
      if (fromCookie && fromCookie !== role) setRoleState(fromCookie);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [role]);

  const setRole = useCallback((next: Role) => {
    writeCookie(next);
    setRoleState(next);
  }, []);

  return { role, setRole };
}
